import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';

import { apiClient, type ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { maskCNPJ, maskPhone, maskCEP, normalizePhoneDigits, onlyDigits } from '@/lib/masks';
import { Button } from '@/components/ui/button';
import { Field, TextInput } from '@/components/clients/form-primitives';
import { MaskedInput } from '@/components/ui/masked-input';

interface OfficeFormState {
  razaoSocial: string;
  nomeFantasia: string;
  cnpj: string;
  email: string;
  telefone: string;
  celular: string;
  oabTitular: string;
  nomeTitular: string;
  endereco: string;
  cidade: string;
  estado: string;
  cep: string;
  site: string;
}

const EMPTY: OfficeFormState = {
  razaoSocial: '',
  nomeFantasia: '',
  cnpj: '',
  email: '',
  telefone: '',
  celular: '',
  oabTitular: '',
  nomeTitular: '',
  endereco: '',
  cidade: '',
  estado: '',
  cep: '',
  site: '',
};

function settingsToForm(settings: Record<string, unknown>): OfficeFormState {
  const s = (k: string) => {
    const v = settings[k];
    return typeof v === 'string' ? v : '';
  };
  // Campos que usam MaskedInput precisam de digitos limpos no state.
  const rawCnpj = s('cnpj');
  const rawTel = s('telefone');
  const rawCel = s('celular');
  const rawCep = s('cep');
  return {
    razaoSocial: s('razaoSocial') || s('razao_social'),
    nomeFantasia: s('nomeFantasia') || s('nome_fantasia'),
    cnpj: onlyDigits(rawCnpj).slice(0, 14),
    email: s('email'),
    telefone: normalizePhoneDigits(rawTel),
    celular: normalizePhoneDigits(rawCel),
    oabTitular: s('oabTitular') || s('oab_titular'),
    nomeTitular: s('nomeTitular') || s('nome_titular'),
    endereco: s('endereco') || s('enderecoCompleto') || s('endereco_completo'),
    cidade: s('cidade'),
    estado: s('estado') || s('uf'),
    cep: onlyDigits(rawCep).slice(0, 8),
    site: s('site'),
  };
}

export function OfficeDataPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();

  const [form, setForm] = useState<OfficeFormState>(EMPTY);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const { data: tenant, isLoading, error } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => {
      if (!tenantId) throw new Error('Sem tenant');
      return apiClient.tenantsGet(tenantId);
    },
    enabled: !!tenantId,
  });

  useEffect(() => {
    if (tenant?.settings) {
      setForm(settingsToForm(tenant.settings));
    }
  }, [tenant]);

  const mutation = useMutation({
    mutationFn: (settings: Record<string, unknown>) => {
      if (!tenantId) throw new Error('Sem tenant');
      return apiClient.tenantsUpdateSettings(tenantId, settings);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['tenant', tenantId], updated);
      setSuccessMsg('Dados salvos com sucesso.');
      setTimeout(() => setSuccessMsg(null), 3000);
    },
  });

  function set(field: keyof OfficeFormState) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  function setMasked(field: keyof OfficeFormState) {
    return (clean: string) => setForm((prev) => ({ ...prev, [field]: clean }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMsg(null);
    const settings: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(form)) {
      if (typeof v === 'string' && v.trim()) {
        settings[k] = v.trim();
      }
    }
    mutation.mutate(settings);
  }

  if (isLoading) {
    return (
      <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
        Carregando…
      </p>
    );
  }

  if (error) {
    return (
      <p className="px-4 py-8 text-center text-sm text-red-500">
        {(error as ApiError).message ?? 'Erro ao carregar dados do escritório.'}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <Building2 className="h-5 w-5 text-[var(--fg-muted)]" />
        <div>
          <h1 className="text-lg font-semibold text-[var(--fg)]">
            Dados do escritório
          </h1>
          <p className="text-sm text-[var(--fg-muted)]">
            Informações usadas na geração de documentos (variáveis{' '}
            <code className="text-xs">office.*</code>).
          </p>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="space-y-6">
        {mutation.error && (
          <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
            {(mutation.error as ApiError).message ?? 'Erro ao salvar.'}
          </p>
        )}
        {successMsg && (
          <p className="rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-500">
            {successMsg}
          </p>
        )}

        <section className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-5 space-y-4">
          <h2 className="text-sm font-medium text-[var(--fg)]">Identificação</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Razão social">
              <TextInput
                value={form.razaoSocial}
                onChange={set('razaoSocial')}
                placeholder="Escritório Jurídico Ltda."
                maxLength={200}
              />
            </Field>
            <Field label="Nome fantasia">
              <TextInput
                value={form.nomeFantasia}
                onChange={set('nomeFantasia')}
                placeholder="Nome fantasia"
                maxLength={200}
              />
            </Field>
            <Field label="CNPJ">
              <MaskedInput
                value={form.cnpj}
                onChange={setMasked('cnpj')}
                mask={maskCNPJ}
                maxDigits={14}
                placeholder="00.000.000/0001-00"
                inputMode="numeric"
              />
            </Field>
          </div>
        </section>

        <section className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-5 space-y-4">
          <h2 className="text-sm font-medium text-[var(--fg)]">Titular</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nome do titular">
              <TextInput
                value={form.nomeTitular}
                onChange={set('nomeTitular')}
                placeholder="Dr. João Silva"
                maxLength={200}
              />
            </Field>
            <Field label="OAB do titular">
              <TextInput
                value={form.oabTitular}
                onChange={set('oabTitular')}
                placeholder="RS 12345"
                maxLength={30}
              />
            </Field>
          </div>
        </section>

        <section className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-5 space-y-4">
          <h2 className="text-sm font-medium text-[var(--fg)]">Contato</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="E-mail">
              <TextInput
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="contato@escritório.com.br"
                maxLength={200}
              />
            </Field>
            <Field label="Telefone">
              <MaskedInput
                value={form.telefone}
                onChange={setMasked('telefone')}
                mask={maskPhone}
                maxDigits={11}
                placeholder="(51) 3000-0000"
                inputMode="tel"
              />
            </Field>
            <Field label="Celular">
              <MaskedInput
                value={form.celular}
                onChange={setMasked('celular')}
                mask={maskPhone}
                maxDigits={11}
                placeholder="(51) 99000-0000"
                inputMode="tel"
              />
            </Field>
            <Field label="Site">
              <TextInput
                value={form.site}
                onChange={set('site')}
                placeholder="https://escritório.com.br"
                maxLength={200}
              />
            </Field>
          </div>
        </section>

        <section className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-5 space-y-4">
          <h2 className="text-sm font-medium text-[var(--fg)]">Endereço</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Endereço" className="sm:col-span-2">
              <TextInput
                value={form.endereco}
                onChange={set('endereco')}
                placeholder="Rua das Acácias, 123, Sala 45"
                maxLength={300}
              />
            </Field>
            <Field label="Cidade">
              <TextInput
                value={form.cidade}
                onChange={set('cidade')}
                placeholder="Porto Alegre"
                maxLength={100}
              />
            </Field>
            <Field label="Estado (UF)">
              <TextInput
                value={form.estado}
                onChange={set('estado')}
                placeholder="RS"
                maxLength={2}
              />
            </Field>
            <Field label="CEP">
              <MaskedInput
                value={form.cep}
                onChange={setMasked('cep')}
                mask={maskCEP}
                maxDigits={8}
                placeholder="90000-000"
                inputMode="numeric"
              />
            </Field>
          </div>
        </section>

        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Salvando…' : 'Salvar dados'}
          </Button>
        </div>
      </form>
    </div>
  );
}
